/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from "react";

//@Packages
import {
  Button, Card, CardContent,
  CardHeader, Divider, PageLoader
} from "@forge/common";
import { useTranslation } from "react-i18next";

//@Components
import Toast from "../../../reusablecomponents/toast";
import FileDownload, { FileProps } from "../file-download/FileDownload";
import ConfirmationModal from "../../../reusablecomponents/confirmationmodal";

//@Models
import { FileDetailsEntity } from "../../../models/diagnosticDetails";

//@Services
import {
  useGetDownFileSasUriQuery, useGetMetaDataApiQuery, useSendCommandMutation
} from "../../../services/file-service-api/fileServeApi";
import { downloadFile } from "../../../services/file-service-api/fileDownloader";

//@Utils
import { getStatusClass } from "../utils/getStatusClass";
import { convertUTCStringtoFormattedDate } from "../../../utils/utility";
import { getErrorInfo } from "../../../reusablecomponents/errorInfo/getErrorInfo";

//@Constants
import { LOCALE_CONST } from "../../../utils/constants";
import { CommandServiceNetworkErrObj, NetworkErrObj } from "../utils/gatewayTypes";

/**
 * Diagnostics component renders a Diagnostics report for a gateway device.
 * It displays the last execution details, including trigger time and status,
 * and provides an option to run Diagnostics again. and download the reports.
 *
 * @returns {JSX.Element} The rendered Diagnostics component.
 */
const Diagnostics: React.FC = () => {
  const { t } = useTranslation();

  const DELAY_TIMEOUT = 60000;

  const convertFn = (value: string) => t(value);

  const [status, setStatus] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState<string>("");
  const [fileToDownload, setFileToDownload] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showRunDiagnosticsModal, setShowRunDiagnosticsModal] = useState(false);

  const {
    data: diagnosticsMetaData,
    error: diagnosticsMetaDataErr,
    refetch: refetchDiagnosticsMetaData,
    isLoading: diagnosticsMetaDataLoading,
    isFetching: isFetchingDiagnosticsMetaData
  } = useGetMetaDataApiQuery({
    systemGuid: sessionStorage.getItem("systemGuid") || "",
    category: "diagnostic"
  });

  const {
    data: downloadFileSasUri,
    error: downloadFileSasUriErr,
    isLoading: downloadSasUriLoading,
    isFetching: isFetchingDownloadFileSasUri
  } = useGetDownFileSasUriQuery({
    systemGuid: sessionStorage.getItem("systemGuid") || "",
    category: "diagnostic",
    fileName: fileToDownload
  }, { skip: !fileToDownload });

  const [sendCommand,
    {
      data: fetchDiagnostics,
      error: runDiagnosticsError,
      isError: hasRunDiagnosticsError
    }] = useSendCommandMutation();

  const handleDownload = async (sasUri: string) => {
    const { error } = await downloadFile(sasUri, fileToDownload);

    if (error) {
      setErrorTitle("Error");
      setErrorMessage(error);
      setShowToast(true);
    }
  };

  /**
   * useEffect hook to set the status based on the meta data response
   */

  useEffect(() => {
    if (diagnosticsMetaData && diagnosticsMetaData.lastExecutionDetails) {
      setStatus(diagnosticsMetaData.lastExecutionDetails.status);
    }
  }, [diagnosticsMetaData]);

  /**
   *
   * to poll the metadata endpoint to get the latest diagnostics meta data for every minute
   */

  const metadataPolling = () => {
    const interval = setInterval(async() => {
      try {
        await refetchDiagnosticsMetaData();
      } catch (error) {
        //do nothing
      }

      if (
        diagnosticsMetaData?.lastExecutionDetails?.status === "Success" ||
        diagnosticsMetaData?.lastExecutionDetails?.status === "Failed"
      ) {
        clearInterval(interval);
      }
    }, DELAY_TIMEOUT); // 60,000 milliseconds = 60 seconds = 1 minute

    return () => {
      clearInterval(interval); // Cleanup interval
    };
  };

  /**
   *
   * useEffect hook to call the metadataPolling function
   */

  useEffect(() => {
    const isFetchDiagnosticInitiated =
      diagnosticsMetaData?.lastExecutionDetails?.status === "Initiated";

    const isFetchDiagnosticSuccess =
      diagnosticsMetaData?.lastExecutionDetails?.status === "Success";

    const isFetchDiagnosticFailed =
      diagnosticsMetaData?.lastExecutionDetails?.status === "Failed";

    if (fetchDiagnostics || isFetchDiagnosticInitiated || isFetchDiagnosticSuccess || isFetchDiagnosticFailed) {
      if (isFetchDiagnosticInitiated || isFetchDiagnosticSuccess || isFetchDiagnosticFailed) {
        setIsLoading(false);
      }
      metadataPolling();
    }
  }, [diagnosticsMetaData, fetchDiagnostics]);

  /**
   * useEffect hook to download the file after getting the sas uri
   */
  useEffect(() => {
    if (downloadFileSasUri) {
      handleDownload(downloadFileSasUri.sasUri);
      setFileToDownload("");
    }
  }, [downloadFileSasUri]);

  /**
   * To trigger send command api which sends the command to the IOT Device
   */
  const handleConfirm = async () => {
    setShowRunDiagnosticsModal(false);
    setIsLoading(true);
    try {
      await sendCommand({
        systemGuid: sessionStorage.getItem("systemGuid") || "",
        category: "diagnostic"
      }).unwrap();
    } catch (err) { }
  };

  /**
   * To set the error for toast
   * @param statusCode number
   */
  const displayErrorToast = (statusCode: number, errMsg?: string, fetchConfig?: boolean) => {
    const errObj = getErrorInfo(statusCode, convertFn);
    if (!fetchConfig) {
      setErrorTitle(errObj.title);
      setErrorMessage(errMsg || errObj.description);
    } else {
      setErrorTitle(t(LOCALE_CONST.noRespReceived));
      setErrorMessage(t(LOCALE_CONST.noRespReceivedSystem));
    }
  };

  const getErrorObjValue = (error: NetworkErrObj) => {
    if (!error) {
      return "";
    } else if (typeof error.data === "object") {
      return error.data?.Message || "";
    } else if (typeof error.data === "string") {
      return error.data || "";
    } else {
      return "";
    }
  };

  /**
   * Network error handling for metadata endpoint, run diagnostic, and download file
   */
  useEffect(() => {
    if (diagnosticsMetaDataErr) {
      setIsLoading(false);
      displayErrorToast(
        (diagnosticsMetaDataErr as NetworkErrObj)?.status,
        getErrorObjValue(diagnosticsMetaDataErr as NetworkErrObj),
        false
      );
    }
    if (runDiagnosticsError) {
      setIsLoading(false);
      displayErrorToast(
        (runDiagnosticsError as CommandServiceNetworkErrObj)?.status,
        (runDiagnosticsError as CommandServiceNetworkErrObj)?.data?.Error,
        true
      );
    }
    if (downloadFileSasUriErr) {
      displayErrorToast(
        (downloadFileSasUriErr as NetworkErrObj)?.status,
        getErrorObjValue(downloadFileSasUriErr as NetworkErrObj),
        false
      );

      setFileToDownload("");
    }
  }, [
    diagnosticsMetaDataErr, runDiagnosticsError,
    downloadFileSasUriErr, hasRunDiagnosticsError
  ]);

  const isLoadingFn = () => diagnosticsMetaDataLoading || downloadSasUriLoading || isFetchingDiagnosticsMetaData || isFetchingDownloadFileSasUri;

  return (
    <div className="diagnostics-container mb-4">
      {showToast && (
        <div className="toast-container" onClick={() => setShowToast(false)}>
          <Toast id="diagnostics-metadata-toast"
            showClose={true} showDismiss={false}
            title={errorTitle} type={"error"}
            lifetime={5000} subtitle={""} content={errorMessage}
          />
        </div>
      )}
      <PageLoader data-testid="page-loader" isLoading={isLoadingFn()} />
      <Card className="mx-auto w-[70vw] min-w-full max-w-4xl">
        <CardHeader title={t(LOCALE_CONST.reportDiagnostics)}
          titleVariant="alt" className="items-center card-header-title"
          slotRight={(
            <Button variant="secondary" data-testid="run-diagnostics-button"
              onClick={() => setShowRunDiagnosticsModal(true)}
            >
              {t(LOCALE_CONST.reportRunDiagnosticsButtonLabel)}
            </Button>
          )}
        />
        <CardContent>
          <div className="mb-4">{t(LOCALE_CONST.reportDiagnosticsDesc)}</div>
          <div className="flex">
            <div className="w-1/4">{t(LOCALE_CONST.reportTriggerTime)}</div>
            <div className="w-2/3">
              {convertUTCStringtoFormattedDate(
                diagnosticsMetaData?.lastExecutionDetails?.triggerTime || ""
              )}
            </div>
          </div>

          <Divider orientation="horizontal" subtitle=""
            subtitlePosition="left" type="inset" className="my-4"
          />

          <div className="flex h-10 mb-4">
            <div className="w-1/4">{t(LOCALE_CONST.reportStatus)}</div>
            <div className={`w-2/3 ${getStatusClass(status)}`}>
              {status || "-"}
            </div>
          </div>
          {diagnosticsMetaData?.fileDetails ? (
            <div>
              {diagnosticsMetaData.fileDetails.map(
                (file: FileDetailsEntity, index: number) => (
                  <FileDownload key={index} file={file}
                    isInProgress={status === "Initiated"}
                    onDownloadClick={(file: FileProps) => setFileToDownload(file.fileName)}
                  />
                )
              )}
            </div>
          ) : (
            <div>{t(LOCALE_CONST.reportNoDiagnosticsFilesFound)}</div>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal isOpen={showRunDiagnosticsModal}
        onClose={() => setShowRunDiagnosticsModal(false)}
        onConfirm={handleConfirm} title={t(LOCALE_CONST.reportRunDiagnosticsModalTitle)}
        content={(
          <div className="diag-cont-div-cls">
            <p>{t(LOCALE_CONST.reportRunDiagnosticsModalContent)}</p>
          </div>
        )}
      />

      {<PageLoader content="" isLoading={isLoading} />}
    </div>
  );
};

export default Diagnostics;